import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { setElementVars } from '@vanilla-extract/dynamic';
import { useEffect } from 'react';
import type { RefObject } from 'react';
import * as styles from './useEnterAnimationStagger.css';

const DEFAULT_STAGGER_DELAY: Milliseconds = 70;
const ANIMATED_ATTRIBUTE_ACTIVE_STATE = 'active';

interface UseEnterAnimationStaggerProps {
  readonly ref: RefObject<HTMLElement>;
  readonly animate: boolean;
  readonly animationDelay?: Milliseconds;
  readonly staggerDelay?: Milliseconds;
}

export const useEnterAnimationStagger = ({
  ref,
  animationDelay = 0,
  staggerDelay = DEFAULT_STAGGER_DELAY,
  animate,
}: UseEnterAnimationStaggerProps): void => {
  useEffect(() => {
    const element = ref.current;

    if (element == null || !animate) {
      return;
    }

    const raf = requestAnimationFrame(() => {
      const animatedTargets = element.querySelectorAll<HTMLElement>(
        `[${styles.ANIMATED_ATTRIBUTE_NAME}]:not([${styles.ANIMATED_ATTRIBUTE_NAME}="${ANIMATED_ATTRIBUTE_ACTIVE_STATE}"])`,
      );

      animatedTargets.forEach((target, index) => {
        setElementVars(target, {
          [styles.animationDelayVar]: `${animationDelay + (index + 1) * staggerDelay}ms`,
        });
        target.setAttribute(styles.ANIMATED_ATTRIBUTE_NAME, ANIMATED_ATTRIBUTE_ACTIVE_STATE);
      });
    });

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [animate, ref, staggerDelay, animationDelay]);
};
